import { parseJSON } from '@/shared/commonFunctions';
import type { ClientLojaProps, ClientProps, DT_ULTIMA_COMPRA } from '@/types';

/** Campos que variam por loja (ficam em `lojas[]` e são espelhados na raiz a partir de `lojas[0]`). */
const LOJA_FIELD_KEYS = [
  'COD_LOJA',
  'COD_SEGMENTO',
  'COD_RCA',
  'COD_RCA_CLIENTE',
  'COD_TABELA_VENDA',
  'COD_REGIME_TRIB',
  'COD_CONDICAO_MAX',
  'COD_CONDICAO_PADRAO',
  'COD_PAGAMENTO',
  'COD_ROTA',
] as const;

type LojaFieldKey = (typeof LOJA_FIELD_KEYS)[number];

function parseCodSegmento(value: unknown): ClientLojaProps['COD_SEGMENTO'] {
  if (typeof value === 'string') {
    return parseJSON(value);
  }
  return value as ClientLojaProps['COD_SEGMENTO'];
}

function normalizeDtUltimaCompra(value: unknown): DT_ULTIMA_COMPRA[] {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    const parsed = parseJSON(value);
    return Array.isArray(parsed) ? parsed : [];
  }
  return [];
}

function buildLojaEntry(raw: Record<string, unknown>): ClientLojaProps {
  const codRcaClienteRaw = raw.COD_RCA_CLIENTE;
  const codRcaClienteNum =
    codRcaClienteRaw != null && codRcaClienteRaw !== '' ? Number(codRcaClienteRaw) : NaN;

  return {
    COD_LOJA: raw.COD_LOJA as number,
    COD_SEGMENTO: parseCodSegmento(raw.COD_SEGMENTO),
    COD_RCA: raw.COD_RCA as number,
    ...(Number.isFinite(codRcaClienteNum) ? { COD_RCA_CLIENTE: codRcaClienteNum } : {}),
    COD_TABELA_VENDA: raw.COD_TABELA_VENDA as number,
    COD_REGIME_TRIB: raw.COD_REGIME_TRIB as number,
    COD_CONDICAO_MAX: raw.COD_CONDICAO_MAX as number,
    COD_CONDICAO_PADRAO: raw.COD_CONDICAO_PADRAO as number,
    COD_PAGAMENTO: raw.COD_PAGAMENTO as ClientLojaProps['COD_PAGAMENTO'],
    COD_ROTA: raw.COD_ROTA as number,
  };
}

function omitLojaFields<T extends Record<string, unknown>>(row: T): Record<string, unknown> {
  const out = { ...row };
  LOJA_FIELD_KEYS.forEach((k) => {
    delete out[k];
  });
  return out;
}

function pickLojaFields(loja: ClientLojaProps): Pick<ClientProps, LojaFieldKey> {
  return {
    COD_LOJA: loja.COD_LOJA,
    COD_SEGMENTO: loja.COD_SEGMENTO as ClientProps['COD_SEGMENTO'],
    COD_RCA: loja.COD_RCA,
    COD_RCA_CLIENTE: loja.COD_RCA_CLIENTE,
    COD_TABELA_VENDA: loja.COD_TABELA_VENDA,
    COD_REGIME_TRIB: loja.COD_REGIME_TRIB,
    COD_CONDICAO_MAX: loja.COD_CONDICAO_MAX,
    COD_CONDICAO_PADRAO: loja.COD_CONDICAO_PADRAO,
    COD_PAGAMENTO: loja.COD_PAGAMENTO as ClientProps['COD_PAGAMENTO'],
    COD_ROTA: loja.COD_ROTA,
  };
}

/**
 * Agrupa registros da API que repetem COD_CLIENTE com COD_LOJA / COD_SEGMENTO diferentes.
 * Preserva a ordem da API: `lojas[0]` é sempre a primeira linha recebida para aquele cliente.
 */
export function groupClientsByLoja(rawClients: any[]): ClientProps[] {
  if (!Array.isArray(rawClients) || rawClients.length === 0) {
    return [];
  }

  const byCliente = new Map<
    number,
    {
      firstRow: Record<string, unknown>;
      lojas: ClientLojaProps[];
      clientsUltimaCompra: DT_ULTIMA_COMPRA[];
    }
  >();

  for (const row of rawClients) {
    if (row == null || typeof row !== 'object') continue;
    const cod = (row as { COD_CLIENTE?: number }).COD_CLIENTE;
    if (cod == null || Number.isNaN(Number(cod))) continue;

    const codNum = Number(cod);
    const asRecord = row as Record<string, unknown>;
    const lojaEntry = buildLojaEntry(asRecord);

    const dtUltimaCompra = normalizeDtUltimaCompra(row.DT_ULTIMA_COMPRA);
    const existing = byCliente.get(codNum);
    if (!existing) {
      byCliente.set(codNum, {
        firstRow: asRecord,
        lojas: [lojaEntry],
        clientsUltimaCompra: dtUltimaCompra,
      });
    } else {
      existing.lojas.push(lojaEntry);
      existing.clientsUltimaCompra.push(...dtUltimaCompra);
    }
  }

  const result: ClientProps[] = [];

  byCliente.forEach(({ firstRow, lojas, clientsUltimaCompra }) => {
    const base = omitLojaFields(firstRow) as unknown as ClientProps;
    const mirror = lojas.length > 0 ? pickLojaFields(lojas[0]) : ({} as Pick<ClientProps, LojaFieldKey>);
    result.push({
      ...base,
      ...mirror,
      lojas,
      DT_ULTIMA_COMPRA: clientsUltimaCompra || [],
    } as ClientProps);
  });

  return result;
}

/**
 * Garante o formato multi-loja: várias linhas com o mesmo `COD_CLIENTE` viram um único cliente com `lojas[]`.
 * Também normaliza linhas “planas” (sem `lojas`) para o mesmo envelope que `groupClientsByLoja` produz.
 * Se a lista já tiver um cliente por `COD_CLIENTE` com `lojas` preenchido, devolve a mesma referência.
 */
export function ensureClientsGroupedByLoja(clients: unknown[] | null | undefined): ClientProps[] {
  if (!Array.isArray(clients) || clients.length === 0) {
    return [];
  }

  const codCounts = new Map<number, number>();
  for (const row of clients) {
    if (row == null || typeof row !== 'object') continue;
    const cod = Number((row as { COD_CLIENTE?: unknown }).COD_CLIENTE);
    if (!Number.isFinite(cod)) continue;
    codCounts.set(cod, (codCounts.get(cod) || 0) + 1);
  }
  const hasDuplicateCod = [...codCounts.values()].some((n) => n > 1);

  const anyMissingLojas = clients.some((row) => {
    if (row == null || typeof row !== 'object') return false;
    const lojas = (row as { lojas?: unknown }).lojas;
    return !Array.isArray(lojas) || lojas.length === 0;
  });

  if (!hasDuplicateCod && !anyMissingLojas) {
    return clients as ClientProps[];
  }

  return groupClientsByLoja(clients as any[]);
}
